import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    BarChartOutlined,
    BellOutlined,
    DeleteOutlined,
    EditOutlined,
    FileImageOutlined,
    FileOutlined,
    MoreOutlined,
    MutedOutlined,
    PlusOutlined,
    PushpinOutlined
} from '@ant-design/icons'
import { usePartners } from '@entities/partner'
import { checkDraftPost, savePayload } from '@entities/post/api'
import { kktuCodes } from '@entities/post/model/data/kktuCodes'
import OneContract from '@shared/assets/1-contract.svg'
import TwoContract from '@shared/assets/2-contract.svg'
import { advertisingPostType, collaborationPostType } from '@shared/lib/constants/constants'
import { extractLinksFromHTML } from '@shared/lib/extractLinksFromHTML/extractLinksFromHTML'
import { getMessageType } from '@shared/lib/getMessageType/getMessageType'
import { randomId } from '@shared/lib/randomId/randomId'
import { transformToTreeData } from '@shared/lib/transformToTreeData/transformToTreeData'
import { validateLength } from '@shared/lib/validatorLength/validatorLength'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { CustomUpload } from '@shared/ui/custom-upload/custom-upload'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import { TextEditor } from '@shared/ui/text-editor/text-editor'
import type { FormProps, UploadFile, UploadProps } from 'antd'
import {
    Avatar,
    Button,
    Col,
    DatePicker,
    Divider,
    Dropdown,
    Flex,
    Form,
    Image,
    Input,
    InputNumber,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Switch,
    theme,
    Tooltip,
    TreeSelect,
    Typography
} from 'antd'
import type { UploadChangeParam } from 'antd/lib/upload'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePosts } from '../../model/hooks/use-posts'
import type {
    AdLink,
    AdPriceMethod,
    FileDTO,
    Message,
    OrdResourceType,
    Post,
    PostState,
    Reallocation
} from '../../model/types'
import { MessageType } from '../../model/types'
const { Text, Paragraph, Title, Link } = Typography

interface EditAdsPostModalProps {
    open: boolean
    editPostData: Post
    adCampaignName: string
    onClose: () => void
    onSuccess?: () => void
}

type FieldType = {
    title: string
    state: PostState
    channels: number[]
    messages: Message[]
    publishDate: Dayjs
    notify: boolean
    isPinned: boolean
    autoDeleteHours: number
    keepOnTopHours: number
    isAdvertising: boolean
    adCampaign: number
    adCampaignName: string
    advertisingInfo: {
        budget: number
        viewToken: string
        sellerConfirm: boolean
        customerConfirm: boolean
        paymentConfirmed: boolean
        priceMethod: AdPriceMethod
        adCampaign: number
        links: AdLink[]
        ord: {
            withAgent: boolean
            category: string
            agentCommission: number
            resourceName: string
            resourceUrl: string
            resourceType: OrdResourceType
            reallocations: Reallocation[]
            nds: boolean
            ndsPercent: number
        }
    }
    postType: {
        value: number
    }
}

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const EditAdsPostModal = ({ open, onClose, adCampaignName, editPostData }: EditAdsPostModalProps) => {
    const {
        actions: { editPost },
        postTypes,
        editPostIsLoading
    } = usePosts()

    const [form] = Form.useForm()

    const {
        token: { colorTextLabel, colorPrimary }
    } = theme.useToken()

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async (values: FieldType) => {
            await editPost({
                ...values,
                id: editPostData.id,
                publishDate: values.publishDate.toDate(),
                advertisingInfo: {
                    ...values.advertisingInfo,
                    sellerConfirm: false,
                    customerConfirm: false
                }
            })

            onClose()
        },
        [editPost, editPostData.id, onClose]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const handleDelete = useCallback((remove: (name: number) => void, name: number) => {
        remove(name)
    }, [])

    const normalizeFile = (e: UploadChangeParam<UploadFile<FileDTO>>) => (Array.isArray(e) ? e : e?.fileList)

    const getUploadProps = ({ accept, multiple = true }: UploadProps<FileDTO>): UploadProps<FileDTO> => ({
        multiple,
        maxCount: multiple ? 99 : 1,
        action: '/api/posts/uploads',
        listType: 'picture-card',
        accept
    })

    const handleSetMultiValue = (index: number, value: boolean) => {
        const messages = form.getFieldValue('messages') || []

        messages[index] = {
            ...messages[index],
            isCaptionAbove: value
        }

        form.setFieldsValue({ messages })
    }

    const [values, setValues] = useState<FieldType>()
    const [repostModalOpen, setRepostModalOpen] = useState(false)
    const [answerModalOpen, setAnswerModalOpen] = useState(false)

    const budget = form.getFieldValue('advertisingInfo')?.budget

    const { partnersResources } = usePartners({ loadPartnersResources: true })

    const [createPostUsingBot, setCreatePostUsingBot] = useState(false)

    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    const [postVersion, setPostVersion] = useState(0)

    useEffect(() => {
        const links = extractLinksFromHTML(values?.messages?.[0].text)

        form.setFieldsValue({
            advertisingInfo: {
                links
            }
        })
    }, [form, values?.messages?.[0].text])

    const onCreatePostUsingBot = async () => {
        const sessionId = randomId()
        const payloadId = await savePayload({
            action: 'edit_post',
            session_id: sessionId,
            platform: 'desktop',
            post_id: editPostData.id
        })

        window.open(`https://t.me/${BOT_USERNAME}?start=payload_${payloadId}`)

        intervalRef.current = setInterval(async () => {
            const postData = await checkDraftPost(sessionId)

            if (postData) {
                if (postVersion !== postData.version) {
                    form.setFieldsValue({
                        messages: [
                            {
                                ...postData.post,
                                id: Date.now(),
                                file: postData.post.file.map((file) => {
                                    const item = {
                                        ...file,
                                        name: file.response.file_name,
                                        type: file.response.file_type,
                                        url: file.response.file_url
                                    }
                                    if (
                                        file.response.file_type !== MessageType.AUDIO &&
                                        file.response.file_type !== MessageType.VOICE &&
                                        file.response.file_type !== MessageType.DOCUMENT
                                    ) {
                                        item.thumbUrl = file.response?.preview_url || file.response.file_url
                                    }
                                    return item
                                })
                            }
                        ]
                    })
                    setCreatePostUsingBot(false)
                    setPostVersion(postData.version)
                }
            }
        }, 3000)
    }

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [])

    const isOrd = Form.useWatch('isOrd', form)

    return (
        <>
            <Modal
                open={open}
                centered
                onCancel={onClose}
                title="Редактирование поста"
                footer={[
                    <Button key="back" onClick={onClose}>
                        Отменить
                    </Button>,
                    <Button
                        type="primary"
                        key="submit"
                        onClick={() => onSubmit({ state: 'draft' })}
                        disabled={!values?.publishDate}
                        loading={editPostIsLoading}
                    >
                        Сохранить
                    </Button>
                ]}
                width={isOrd ? 1200 : 600}
                destroyOnHidden
            >
                <Form
                    form={form}
                    name="add-post"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    onFinish={onFinish}
                    initialValues={{
                        ...editPostData,
                        publishDate: dayjs(editPostData?.publishDate),
                        postType: {
                            label: editPostData.postType?.name,
                            value: editPostData.postType?.value
                        },
                        messages: editPostData?.messages.map((item) => ({
                            ...item,
                            file: item.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        })),
                        isOrd: !!editPostData.advertisingInfo?.ordInfo,
                        adCampaignName,
                        advertisingInfo: {
                            ...editPostData.advertisingInfo,
                            ord: editPostData.advertisingInfo?.ordInfo
                        }
                    }}
                    onValuesChange={(_, values: FieldType) => setValues(values)}
                >
                    <Row gutter={16}>
                        <Col span={isOrd ? 12 : 24}>
                            <Row gutter={8} style={{ marginBottom: 16 }}>
                                <Col>
                                    <CustomTag>
                                        {budget > 0 ? advertisingPostType.name : collaborationPostType.name}
                                    </CustomTag>
                                </Col>
                                <Col>
                                    <Form.Item<FieldType> name="postType" required={false} style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="Выберите тип"
                                            labelInValue
                                            optionLabelProp="label"
                                            popupMatchSelectWidth={false}
                                        >
                                            {postTypes?.map((item) => (
                                                <Select.Option
                                                    style={{ textAlign: 'center' }}
                                                    key={item.id}
                                                    value={item.id}
                                                    label={item.name}
                                                >
                                                    <CustomTag color={item.color}>{item.name}</CustomTag>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col style={{ marginLeft: 'auto' }}>
                                    <Form.Item
                                        label="Режим ОРД"
                                        name="isOrd"
                                        valuePropName="checked"
                                        labelAlign="left"
                                        labelCol={{ flex: 'auto' }}
                                        wrapperCol={{ flex: 'none' }}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item<FieldType> name="state" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item<FieldType> name="isAdvertising" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item<FieldType> name={['advertisingInfo', 'adCampaign']} hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item<FieldType> name="adCampaignName">
                                <Input placeholder="Введите название рекламной компании" disabled />
                            </Form.Item>
                            <Divider />
                            <Row gutter={16} align={'bottom'}>
                                <Col span={16}>
                                    <Form.Item<FieldType>
                                        label="Выберите рекламную площадку"
                                        name="channels"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            style={{ width: '100%' }}
                                            disabled
                                            popupMatchSelectWidth={false}
                                        >
                                            {partnersResources?.map((c) => (
                                                <Select.Option value={c.id} key={c.id}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar src={c.photoUrl} size={20} />
                                                        {c.name}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={10} style={{ position: 'relative' }}>
                                    <Form.Item<FieldType>
                                        label="Время публикации"
                                        name="publishDate"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <DatePicker
                                            showTime
                                            showSecond={false}
                                            style={{ width: '100%' }}
                                            format={[
                                                'DD/MM/YYYY HH:mm',
                                                'DD/MM/YY HH:mm',
                                                'DD.MM.YYYY HH:mm',
                                                'DD.MM.YY HH:mm'
                                            ]}
                                            minDate={dayjs()}
                                            placeholder="Выберите дату публикации"
                                        />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        name="isPinned"
                                        required={false}
                                        style={{ position: 'absolute', top: 0, right: 0 }}
                                    >
                                        <IconSwitch text={'Закрепить'}>
                                            <PushpinOutlined />
                                        </IconSwitch>
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        name="notify"
                                        required={false}
                                        style={{ position: 'absolute', top: 0, right: '26px' }}
                                    >
                                        <IconSwitch text={'Отправить со звуком'}>
                                            <BellOutlined />
                                        </IconSwitch>
                                    </Form.Item>
                                </Col>
                                <Col span={7}>
                                    <Form.Item<FieldType>
                                        label="Держать в топе, ч"
                                        name="keepOnTopHours"
                                        required={false}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>

                                <Col span={7}>
                                    <Form.Item<FieldType>
                                        label="Удалить через, ч"
                                        name="autoDeleteHours"
                                        required={false}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.List name="messages">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }, index) => {
                                            const defaultType = form.getFieldValue('messages')[index].messageType
                                            const multi = form.getFieldValue('messages')[index].multi
                                            const messages = form.getFieldValue('messages')
                                            const isCaptionAbove = form.getFieldValue('messages')[index]?.isCaptionAbove
                                            const item =
                                                (messages[0]?.file && getMessageType(messages[0]?.file[0])) ||
                                                defaultType
                                            const captionItem = (
                                                <>
                                                    <Flex
                                                        style={{
                                                            marginBottom: '10px'
                                                        }}
                                                        gap="middle"
                                                        justify={'space-between'}
                                                        align="center"
                                                    >
                                                        <Text style={{ color: colorTextLabel }}>
                                                            Введите подпись(макс. 1024 символов)
                                                        </Text>
                                                        {!isCaptionAbove && (
                                                            <Button
                                                                type="primary"
                                                                onClick={() => handleSetMultiValue(index, true)}
                                                                icon={<ArrowUpOutlined />}
                                                            />
                                                        )}
                                                    </Flex>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'text']}
                                                        required={false}
                                                        rules={[
                                                            {
                                                                validator: validateLength({ length: 1024 })
                                                            }
                                                        ]}
                                                    >
                                                        <TextEditor viewToken={values?.advertisingInfo.viewToken} />
                                                    </Form.Item>
                                                    {isCaptionAbove && (
                                                        <Button
                                                            style={{
                                                                marginTop: '5px',
                                                                position: 'absolute',
                                                                right: 0,
                                                                zIndex: 1
                                                            }}
                                                            type="primary"
                                                            onClick={() => handleSetMultiValue(index, false)}
                                                            icon={<ArrowDownOutlined />}
                                                        />
                                                    )}
                                                </>
                                            )

                                            const moreBtn = (
                                                <Dropdown
                                                    trigger={['click']}
                                                    menu={{
                                                        items: [
                                                            {
                                                                key: 1,
                                                                label: (
                                                                    <Space
                                                                        style={{ width: '100%' }}
                                                                        onClick={() => setRepostModalOpen(true)}
                                                                    >
                                                                        Репост
                                                                    </Space>
                                                                )
                                                            },
                                                            {
                                                                key: 2,
                                                                label: (
                                                                    <Space
                                                                        style={{ width: '100%' }}
                                                                        onClick={() => setAnswerModalOpen(true)}
                                                                    >
                                                                        Ответ
                                                                    </Space>
                                                                )
                                                            },
                                                            { type: 'divider' },
                                                            {
                                                                key: 3,
                                                                label: 'Тип сообщения',
                                                                type: 'group',
                                                                children: [
                                                                    {
                                                                        key: 4,
                                                                        label: (
                                                                            <Space>
                                                                                <EditOutlined /> Текст
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            handleDelete(remove, name)
                                                                            add({
                                                                                id: Date.now(),
                                                                                messageType: MessageType.TEXT
                                                                            })
                                                                        }
                                                                    },
                                                                    {
                                                                        key: 5,
                                                                        label: (
                                                                            <Space>
                                                                                <BarChartOutlined /> Опрос
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            handleDelete(remove, name)
                                                                            add({
                                                                                id: Date.now(),
                                                                                messageType: MessageType.QUIZ
                                                                            })
                                                                        }
                                                                    },
                                                                    {
                                                                        key: 6,
                                                                        label: (
                                                                            <Space>
                                                                                <FileImageOutlined /> Фото и Видео
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            handleDelete(remove, name)
                                                                            add({
                                                                                id: Date.now(),
                                                                                messageType: MessageType.PHOTO,
                                                                                multi: true,
                                                                                isCaptionAbove: false
                                                                            })
                                                                        }
                                                                    },
                                                                    {
                                                                        key: 7,
                                                                        label: (
                                                                            <Space>
                                                                                <MutedOutlined /> Аудио
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            handleDelete(remove, name)
                                                                            add({
                                                                                id: Date.now(),
                                                                                messageType: MessageType.AUDIO,
                                                                                multi: true,
                                                                                isCaptionAbove: false
                                                                            })
                                                                        }
                                                                    },
                                                                    {
                                                                        key: 8,
                                                                        label: (
                                                                            <Space>
                                                                                <FileOutlined /> Документы
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            handleDelete(remove, name)
                                                                            add({
                                                                                id: Date.now(),
                                                                                messageType: MessageType.DOCUMENT,
                                                                                multi: true,
                                                                                isCaptionAbove: false
                                                                            })
                                                                        }
                                                                    }
                                                                ]
                                                            }
                                                        ]
                                                    }}
                                                >
                                                    <Button type="text" style={{ padding: '4px' }}>
                                                        <MoreOutlined />
                                                    </Button>
                                                </Dropdown>
                                            )

                                            return (
                                                <div key={key} style={{ marginBottom: 16, position: 'relative' }}>
                                                    <Modal
                                                        title="Репост сообщения"
                                                        open={repostModalOpen}
                                                        onCancel={() => setRepostModalOpen(false)}
                                                        onOk={() => {
                                                            form.validateFields([name, 'repostLink']).then(() =>
                                                                setRepostModalOpen(false)
                                                            )
                                                        }}
                                                        centered
                                                    >
                                                        <Paragraph>
                                                            Ссылка на сообщение в формате t.me/username/123 или
                                                            t.me/c/123/123. Для репоста канала или чата нужно чтобы боты
                                                            был в админке.
                                                        </Paragraph>
                                                        <Form.Item
                                                            name={[name, 'repostLink']}
                                                            required={false}
                                                            rules={[{ required: true, message: 'Обязательное поле' }]}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Modal>
                                                    <Modal
                                                        title="Ответ на сообщение"
                                                        open={answerModalOpen}
                                                        onCancel={() => setAnswerModalOpen(false)}
                                                        onOk={() => {
                                                            form.validateFields([name, 'replyLink']).then(() =>
                                                                setAnswerModalOpen(false)
                                                            )
                                                        }}
                                                        centered
                                                    >
                                                        <Paragraph>
                                                            Ссылка на сообщение в формате t.me/username/123 или
                                                            t.me/c/123/123.
                                                        </Paragraph>
                                                        <Form.Item
                                                            name={[name, 'replyLink']}
                                                            required={false}
                                                            rules={[{ required: true, message: 'Обязательное поле' }]}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Modal>
                                                    <div style={{ position: 'relative' }}>
                                                        <Space
                                                            style={{
                                                                position: 'absolute',
                                                                top: '4px',
                                                                right: 0,
                                                                zIndex: 1
                                                            }}
                                                        >
                                                            <Text>Редактировать пост через бота</Text>
                                                            <Switch
                                                                value={createPostUsingBot}
                                                                onChange={() => setCreatePostUsingBot((prev) => !prev)}
                                                            />
                                                        </Space>
                                                        {createPostUsingBot ? (
                                                            <div
                                                                style={{
                                                                    padding: '40px 50px 50px 50px',
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <Button size="large" onClick={onCreatePostUsingBot}>
                                                                    Перейти в бот
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {item === MessageType.TEXT && (
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'text']}
                                                                        label={
                                                                            <Space size={2}>
                                                                                <Text style={{ color: colorTextLabel }}>
                                                                                    Содержание сообщения
                                                                                </Text>
                                                                                {moreBtn}
                                                                            </Space>
                                                                        }
                                                                        required={false}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Содержание обязательно',
                                                                                validator: validateLength({
                                                                                    length: 4096,
                                                                                    required: true
                                                                                })
                                                                            }
                                                                        ]}
                                                                    >
                                                                        <TextEditor
                                                                            viewToken={
                                                                                values?.advertisingInfo.viewToken
                                                                            }
                                                                            maxLength={4096}
                                                                        />
                                                                    </Form.Item>
                                                                )}
                                                                {(item === MessageType.PHOTO ||
                                                                    item === MessageType.VIDEO) && (
                                                                    <>
                                                                        {isCaptionAbove && captionItem}
                                                                        <Form.Item
                                                                            {...restField}
                                                                            name={[name, 'file']}
                                                                            valuePropName="fileList"
                                                                            getValueFromEvent={normalizeFile}
                                                                            label={
                                                                                <Space size={2}>
                                                                                    <Text
                                                                                        style={{
                                                                                            color: colorTextLabel
                                                                                        }}
                                                                                    >
                                                                                        Фото и Видео
                                                                                    </Text>{' '}
                                                                                    {moreBtn}
                                                                                </Space>
                                                                            }
                                                                            required={false}
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Файл обязателен'
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <CustomUpload
                                                                                {...getUploadProps({
                                                                                    accept: 'image/*, video/*',
                                                                                    multiple: multi
                                                                                })}
                                                                            />
                                                                        </Form.Item>
                                                                        {!isCaptionAbove && captionItem}
                                                                    </>
                                                                )}
                                                                {item === MessageType.DOCUMENT && (
                                                                    <>
                                                                        {isCaptionAbove && captionItem}
                                                                        <Form.Item
                                                                            {...restField}
                                                                            name={[name, 'file']}
                                                                            valuePropName="fileList"
                                                                            getValueFromEvent={normalizeFile}
                                                                            label={
                                                                                <Space size={2}>
                                                                                    <Text
                                                                                        style={{
                                                                                            color: colorTextLabel
                                                                                        }}
                                                                                    >
                                                                                        Документ
                                                                                    </Text>{' '}
                                                                                    {moreBtn}
                                                                                </Space>
                                                                            }
                                                                            required={false}
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Файл обязателен'
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <CustomUpload
                                                                                {...getUploadProps({
                                                                                    accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,*.*',
                                                                                    multiple: multi
                                                                                })}
                                                                            />
                                                                        </Form.Item>
                                                                        {!isCaptionAbove && captionItem}
                                                                    </>
                                                                )}
                                                                {item === MessageType.AUDIO && (
                                                                    <>
                                                                        {isCaptionAbove && captionItem}
                                                                        <Form.Item
                                                                            {...restField}
                                                                            name={[name, 'file']}
                                                                            valuePropName="fileList"
                                                                            getValueFromEvent={normalizeFile}
                                                                            label={
                                                                                <Space size={2}>
                                                                                    <Text
                                                                                        style={{
                                                                                            color: colorTextLabel
                                                                                        }}
                                                                                    >
                                                                                        Аудио
                                                                                    </Text>{' '}
                                                                                    {moreBtn}
                                                                                </Space>
                                                                            }
                                                                            required={false}
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Файл обязателен'
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <CustomUpload
                                                                                {...getUploadProps({
                                                                                    accept: 'audio/*, .ogg'
                                                                                })}
                                                                            />
                                                                        </Form.Item>
                                                                        {!isCaptionAbove && captionItem}
                                                                    </>
                                                                )}
                                                                {item === MessageType.QUIZ && (
                                                                    <>
                                                                        <Form.Item
                                                                            {...restField}
                                                                            name={[name, 'question']}
                                                                            label={
                                                                                <Space size={2}>
                                                                                    <Text
                                                                                        style={{
                                                                                            color: colorTextLabel
                                                                                        }}
                                                                                    >
                                                                                        Заголовок опроса
                                                                                    </Text>{' '}
                                                                                    {moreBtn}
                                                                                </Space>
                                                                            }
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Заголовок обязателен'
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <Input placeholder="Введите заголовок опроса" />
                                                                        </Form.Item>

                                                                        <Form.List name={[name, 'options']}>
                                                                            {(fields, { add, remove }) => (
                                                                                <>
                                                                                    {fields.map(
                                                                                        ({
                                                                                            key,
                                                                                            name,
                                                                                            ...restField
                                                                                        }) => (
                                                                                            <Form.Item
                                                                                                key={key}
                                                                                                {...restField}
                                                                                                name={name}
                                                                                                rules={[
                                                                                                    {
                                                                                                        required: true,
                                                                                                        message:
                                                                                                            'Вариант обязателен'
                                                                                                    }
                                                                                                ]}
                                                                                            >
                                                                                                <Input
                                                                                                    placeholder={`Введите текст варианта ${name + 1}`}
                                                                                                    addonAfter={
                                                                                                        <Button
                                                                                                            style={{
                                                                                                                height: 'auto'
                                                                                                            }}
                                                                                                            type="link"
                                                                                                            onClick={() =>
                                                                                                                remove(
                                                                                                                    name
                                                                                                                )
                                                                                                            }
                                                                                                            icon={
                                                                                                                <DeleteOutlined />
                                                                                                            }
                                                                                                        />
                                                                                                    }
                                                                                                />
                                                                                            </Form.Item>
                                                                                        )
                                                                                    )}
                                                                                    <Form.Item>
                                                                                        <Button
                                                                                            type="dashed"
                                                                                            onClick={() => add()}
                                                                                            block
                                                                                            icon={<PlusOutlined />}
                                                                                        >
                                                                                            Добавить вариант
                                                                                        </Button>
                                                                                    </Form.Item>
                                                                                </>
                                                                            )}
                                                                        </Form.List>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </>
                                )}
                            </Form.List>
                            <Row gutter={8} align={'bottom'}>
                                <Col span={8}>
                                    <Form.Item<FieldType>
                                        label="Стоимость"
                                        name={['advertisingInfo', 'budget']}
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={4}>
                                    <Form.Item<FieldType> label="Без НДС" name={['advertisingInfo', 'ord', 'nds']}>
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Form.Item<FieldType> name={['advertisingInfo', 'ord', 'ndsPercent']} hidden>
                                    <Input />
                                </Form.Item>
                                <Col span={7}>
                                    <Form.Item name={['advertisingInfo', 'priceMethod']} label="Тип цены">
                                        <Radio.Group
                                            options={[
                                                {
                                                    label: 'Фикс',
                                                    value: 'fix'
                                                },
                                                { label: 'CPM', value: 'cpm' }
                                            ]}
                                            optionType="button"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item name={['advertisingInfo', 'paymentConfirmed']} label="Статус оплаты">
                                        <Select
                                            placeholder="Выберите статус"
                                            allowClear
                                            options={[
                                                { label: 'Оплачен', value: true },
                                                { label: 'Не оплачен', value: false }
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        label="Привяжите/выберите ссылки в тексте для сбора статистики"
                                        shouldUpdate
                                    >
                                        {({ getFieldValue }) => {
                                            const links = getFieldValue(['advertisingInfo', 'links']) || []

                                            return (
                                                <Form.List name={['advertisingInfo', 'links']}>
                                                    {(fields, { update }) => (
                                                        <Row gutter={[16, 16]}>
                                                            {fields.map((field, index) => {
                                                                const link = links[index]
                                                                return (
                                                                    <>
                                                                        <Col span={12}>
                                                                            <Tooltip title={link.href}>
                                                                                <Text style={{ color: colorPrimary }}>
                                                                                    {link.text}
                                                                                </Text>
                                                                            </Tooltip>
                                                                        </Col>
                                                                        <Col span={12}>
                                                                            <div
                                                                                style={{
                                                                                    marginLeft: 16,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 8
                                                                                }}
                                                                            >
                                                                                <Text style={{ color: colorPrimary }}>
                                                                                    Общая
                                                                                </Text>
                                                                                <Form.Item
                                                                                    name={[field.name, 'isUnique']}
                                                                                    valuePropName="checked"
                                                                                    noStyle
                                                                                >
                                                                                    <Switch />
                                                                                </Form.Item>
                                                                                <Text style={{ color: colorPrimary }}>
                                                                                    Уникальная
                                                                                </Text>
                                                                            </div>
                                                                        </Col>
                                                                    </>
                                                                )
                                                            })}
                                                        </Row>
                                                    )}
                                                </Form.List>
                                            )
                                        }}
                                    </Form.Item>
                                </Col>
                                <Form.Item<FieldType> name={['advertisingInfo', 'links']}></Form.Item>
                            </Row>
                        </Col>
                        {isOrd && (
                            <Col span={isOrd ? 12 : 24}>
                                <Title level={3} style={{ marginTop: 0 }}>
                                    Для оператора рекламных данных
                                </Title>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name={['advertisingInfo', 'ord', 'withAgent']}
                                            label="Выберите тип договора"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Обязательное поле'
                                                }
                                            ]}
                                        >
                                            <Radio.Group
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 8
                                                }}
                                                options={[
                                                    {
                                                        value: false,
                                                        label: <Image src={OneContract} preview={false} />
                                                    },
                                                    {
                                                        value: true,
                                                        label: <Image src={TwoContract} preview={false} />
                                                    }
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, curr) => prev.withAgent !== curr.withAgent}
                                        >
                                            {({ getFieldValue }) =>
                                                getFieldValue(['advertisingInfo', 'ord', 'withAgent']) ? (
                                                    <Form.Item
                                                        name={['advertisingInfo', 'ord', 'agentCommission']}
                                                        label="Сумма комиссии агента"
                                                        rules={[{ required: true, message: 'Укажите комиссию агента' }]}
                                                    >
                                                        <InputNumber min={0} style={{ width: '100%' }} addonAfter="₽" />
                                                    </Form.Item>
                                                ) : null
                                            }
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}></Col>

                                    <Col span={24}>
                                        <Row gutter={8} align="bottom">
                                            <Col span={12}>
                                                <Form.Item
                                                    noStyle
                                                    dependencies={[['advertisingInfo', 'ord', 'resourceUrl']]} // <-- слушаем url
                                                >
                                                    {({ getFieldValue }) => {
                                                        const resName =
                                                            getFieldValue(['advertisingInfo', 'ord', 'resourceUrl']) ||
                                                            ''
                                                        return (
                                                            <Form.Item
                                                                label={
                                                                    <Link style={{ fontSize: 17 }}>
                                                                        {resName || 'Название площадки'}
                                                                    </Link>
                                                                }
                                                                name={['advertisingInfo', 'ord', 'resourceName']}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: 'Укажи название площадки'
                                                                    }
                                                                ]}
                                                            >
                                                                <Input placeholder="Название площадки" />
                                                            </Form.Item>
                                                        )
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    label={
                                                        <Text style={{ fontSize: 17, color: colorPrimary }}>
                                                            Тип площадки
                                                        </Text>
                                                    }
                                                    name={['advertisingInfo', 'ord', 'resourceType']}
                                                    valuePropName="checked"
                                                    getValueProps={(v) => ({ checked: v === 'site' })} // строка → булево для Switch
                                                    getValueFromEvent={(checked: boolean) => (checked ? 'site' : 'app')} // булево → строка в форму
                                                >
                                                    <Switch checkedChildren="Сайт" unCheckedChildren="Приложение" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item
                                            label="Категория рекламируемого товара или услуги"
                                            name={['advertisingInfo', 'ord', 'category']}
                                            rules={[{ required: true, message: 'Обязательное поле' }]}
                                        >
                                            <TreeSelect
                                                treeData={transformToTreeData(kktuCodes)}
                                                placeholder="Выберите категорию"
                                                treeExpandAction="click"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Form.Item name={['advertisingInfo', 'ord', 'reallocations']} hidden />
                                    {/* <Col span={24}>
                                        <Form.Item
                                            name={['advertisingInfo', 'ord', 'withReallocation']}
                                            label={
                                                <Space>
                                                    <Text>Разаллокация</Text>
                                                    <Tooltip title="Укажите, какая часть суммы акта/отчёта между исполнителем и заказчиком приходится на интернет-рекламу по атрибутам договора с каждым конечным рекламодателем.">
                                                        <QuestionCircleOutlined />
                                                    </Tooltip>
                                                </Space>
                                            }
                                            valuePropName="checked"
                                            wrapperCol={{ span: 'auto ' }}
                                            labelCol={{ span: 'auto ' }}
                                            required
                                        >
                                            <Switch
                                                onChange={(checked) => {
                                                    if (checked) {
                                                        form.setFieldsValue({
                                                            reallocations: [{}] // сразу один пустой объект
                                                        })
                                                    } else {
                                                        form.setFieldsValue({
                                                            reallocations: []
                                                        })
                                                    }
                                                }}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, curr) =>
                                                prev.withReallocation !== curr.withReallocation
                                            }
                                        >
                                            {({ getFieldValue }) =>
                                                getFieldValue(['advertisingInfo', 'ord', 'withReallocation']) ? (
                                                    <Form.List name={['advertisingInfo', 'ord', 'reallocations']}>
                                                        {(fields, { add, remove }) => (
                                                            <>
                                                                {fields.map(({ key, name, ...restField }) => (
                                                                    <div
                                                                        key={key}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'start',
                                                                            gap: 8,
                                                                            marginBottom: 8
                                                                        }}
                                                                    >
                                                                        <Form.Item
                                                                            {...restField}
                                                                            name={[name, 'amount']}
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Введите сумму'
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <InputNumber
                                                                                min={0}
                                                                                placeholder="Сумма, ₽"
                                                                                style={{ width: '100%' }}
                                                                            />
                                                                        </Form.Item>
                                                                        <Button
                                                                            type="text"
                                                                            onClick={() => remove(name)}
                                                                        >
                                                                            <DeleteOutlined />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                                <Button type="primary" onClick={() => add()}>
                                                                    + Добавить разаллокацию
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Form.List>
                                                ) : null
                                            }
                                        </Form.Item>
                                    </Col> */}
                                </Row>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal>
        </>
    )
}
